<template>
  <div
    class="flex items-center"
    :class="size === 'mini' ? 'space-x-0' : 'space-x-2'"
  >
    <button
      @click="decrement"
      :class="[
        { 'cursor-not-allowed': minReached },
        { 'pointer-events-none opacity-0': hideButtons },
        sizeClasses.buttons,
      ]"
      :disabled="disabled || minReached"
    >
      -
    </button>
    <div class="relative overflow-hidden rounded">
      <input
        :value="formattedValue"
        :class="[sizeClasses.input, { 'ring-ontick-blue-600 ring-2': focus }]"
        @input="handleInput"
        @blur="onBlur"
        @focus="setFocus(true)"
        :disabled="disabled"
        v-validate="'numeric'"
        name="input"
        :errors="errors.first('input')"
      />
      <div
        v-if="loading"
        class="dark:bg-ontick-carbon-500 absolute inset-0 flex items-center justify-center bg-gray-50"
      >
        <i
          v-html="$icons.loading"
          class="text-ontick-blue-600 scale-90 transform"
        ></i>
        <span class="sr-only"> Carregando </span>
      </div>
    </div>
    <button
      @click="increment"
      :class="[
        { 'cursor-not-allowed': maxReached },
        { 'pointer-events-none opacity-0': hideButtons },
        sizeClasses.buttons,
      ]"
      :disabled="disabled || maxReached"
    >
      +
    </button>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: [Number, String],
      required: true,
    },
    max: {
      type: Number,
      required: true,
    },
    min: {
      type: Number,
      required: true,
    },
    step: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    size: {
      type: String,
      default: "normal",
      validator: (value) => {
        return ["full", "normal", "mini"].includes(value);
      },
    },
    currency: {
      type: Boolean,
    },
    normalize: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
    },
    hideButtons: {
      type: Boolean,
    },
  },
  data() {
    return {
      focus: false,
    };
  },
  computed: {
    formattedValue() {
      if (this.currency && !this.focus) {
        return typeof this.value === "string"
          ? this.$filters.currency(this.value.replace(",", "."))
          : this.$filters.currency(this.value);
      } else {
        return typeof this.value === "string"
          ? this.value.replace(",", ".")
          : this.value;
      }
    },
    maxReached() {
      return this.value === this.max;
    },
    minReached() {
      return this.value === this.min;
    },
    splitStep() {
      return this.step.toString().split(".");
    },
    sizeClasses() {
      const sizeList = {
        normal: {
          buttons:
            "bg-ontick-steel-500 h-10 w-10 rounded text-xl font-bold text-white dark:disabled:bg-gray-600 disabled:bg-gray-600 disabled:cursor-not-allowed",
          input:
            "text-ontick-carbon-900 dark:bg-ontick-carbon-500 h-10 w-24 rounded border-none bg-gray-50 text-center dark:text-white font-bold dark:disabled:bg-gray-600 disabled:bg-gray-600 disabled:cursor-not-allowed",
        },
        mini: {
          buttons:
            "rounded text-xl font-bold text-ontick-carbon-900 dark:text-gray-300 px-1",
          input:
            "text-ontick-carbon-900 dark:bg-ontick-carbon-500 h-6 w-full rounded border-none bg-gray-50 text-center dark:text-white text-xs",
        },
        full: {
          buttons:
            "bg-ontick-steel-500 h-10 w-10 rounded text-xl font-bold text-white",
          input:
            "text-ontick-carbon-900 dark:bg-ontick-carbon-500 h-10 w-full rounded border-none bg-gray-50 text-center dark:text-white font-bold",
        },
      };
      return sizeList[this.size];
    },
  },
  methods: {
    setFocus(val) {
      this.focus = val;
    },
    onBlur() {
      this.$emit("blur");
      this.emit(this.filteredValue(Number(this.value)));
      this.setFocus(false);
    },
    normalizeNumber(value, step) {
      const num = Number(
        Number(value).toFixed(this.splitStep[1] ? this.splitStep[1].length : 0)
      );
      const multiplier = this.splitStep[1] ? 10 ** this.splitStep[1].length : 1;
      if (this.normalize && multiplier !== 1) {
        const multipliedNum = this.splitStep[1]
          ? num.toFixed(this.splitStep[1].length) * multiplier
          : num * multiplier;
        const multipliedStep = this.splitStep[1]
          ? step.toFixed(this.splitStep[1].length) * multiplier
          : step * multiplier;
        const formula = multipliedNum - (multipliedNum % multipliedStep);
        const result = formula / multiplier;
        return result;
      } else {
        return num;
      }
    },
    handleInput(e) {
      this.emit(e.target.value);
    },
    formatDecimalNumber(val) {
      if (this.splitStep.length === 2) {
        return Number(val).toFixed(this.splitStep[1].length);
      } else {
        return Number(val);
      }
    },
    decrement() {
      this.$emit("decrement-pressed");
      if (this.value >= this.min) {
        this.emit(this.filteredValue(Number(this.value) - this.step));
      }
    },
    increment() {
      this.$emit("increment-pressed");
      if (this.value <= this.max) {
        this.emit(this.filteredValue(Number(this.value) + this.step));
      }
    },
    toRange(value) {
      if (value > this.max) {
        return this.max;
      } else if (value < this.min) {
        return this.min;
      } else {
        return value;
      }
    },
    filteredValue(value) {
      return this.toRange(
        this.formatDecimalNumber(this.normalizeNumber(Number(value), this.step))
      );
    },
    emit(value) {
      this.$emit("input", value);
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        if (this.initialValue < this.min) {
          console.error(
            `Initial value lower than minimum value (initialValue: ${this.initialValue}, min: ${this.min})`
          );
        }
        if (this.initialValue > this.max) {
          console.error(
            `Initial value higher than maximum value (initialValue: ${this.initialValue}, max: ${this.max})`
          );
        }
        if (this.max < this.min) {
          console.error(
            `Maximum value lower than minimum value (max: ${this.max}, min: ${this.min})`
          );
        }
        if (this.min > this.max) {
          console.error(
            `Minimum value higher than maximum value (max: ${this.max}, min: ${this.min})`
          );
        }
      },
    },
    // value: {
    //   handler(newVal, oldVal) {
    //     console.log(newVal, oldVal);
    //   },
    // },
  },
};
</script>

<style scoped>
.button {
  @apply bg-ontick-steel-500 h-10 w-10 rounded text-xl font-bold text-white;
}
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
